import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { MediaRelationsService } from './media-relations.service';
import { select, Store } from '@ngrx/store';
import {
  MediaRelationsSuggestRequest,
  MediaRelationsSuggestResponse,
} from 'models-media-relations';
import {
  selectMediaRelationsLimit,
  selectMediaRelationsMediaCount,
} from '@epicuro-next/state/p-basket-state';
import { tap, switchMap, withLatestFrom, filter } from 'rxjs/operators';
import { EMPTY, Subject } from 'rxjs';
import { OrderType, Product } from 'graphql-api/gql-api-interfaces';
import { isNonNullish } from '@epicuro-next/utilities';
import { selectActiveAccountId } from '@epicuro-next/state/accounts-state';

interface MediaRelationsState {
  suggestions: MediaRelationsSuggestResponse[];
  mediaCount: number;
  orderType: OrderType;
  fetchedProducts: Set<string>;
  totalProductsFetched: number;
}

@Injectable()
export class MediaRelationsSuggestStore extends ComponentStore<MediaRelationsState> {
  private products$ = new Subject<Product[]>();
  private store = inject(Store);

  constructor(private mediaRelationsService: MediaRelationsService) {
    super({
      suggestions: [],
      mediaCount: 1,
      orderType: OrderType.None,
      fetchedProducts: new Set(),
      totalProductsFetched: 0,
    });
  }

  readonly setMediaCount = this.updater((state, mediaCount: number) => ({
    ...state,
    mediaCount,
  }));

  readonly setOrderType = this.updater((state, orderType: OrderType) => ({
    ...state,
    orderType,
  }));

  readonly updateProducts = this.effect<Product[]>((products$) => {
    return products$.pipe(
      withLatestFrom(
        this.state$,
        this.store.pipe(select(selectMediaRelationsLimit)),
      ),
      filter(([_, __, maxSuggestions]) => isNonNullish(maxSuggestions)),
      switchMap(([products, state, maxSuggestions]) => {
        if (!products || products.length === 0) return EMPTY;

        const newProducts = products.slice(state.totalProductsFetched);
        if (newProducts.length === 0) return EMPTY;
        const startIndex = state.totalProductsFetched + 1;

        const newProductEntries = newProducts.reduce(
          (acc, product, index) => ({
            ...acc,
            [(startIndex + index).toString()]: {
              brandId: product.brandId,
              productId: product.productId,
            },
          }),
          {} as MediaRelationsSuggestRequest['productPositions'],
        );
        const request: MediaRelationsSuggestRequest = {
          productPositions: newProductEntries,
          mediaCount: state.mediaCount,
          orderType: state.orderType,
        };

        return this.mediaRelationsService.suggestMediaRelations(request).pipe(
          tap((newSuggestions) => {
            // Apply the limit before updating the state
            const limitedSuggestions =
              maxSuggestions > 0
                ? newSuggestions.slice(0, maxSuggestions)
                : newSuggestions;

            this.patchState((state) => ({
              suggestions: state.suggestions.concat(limitedSuggestions),
              fetchedProducts: new Set([
                ...Array.from(state.fetchedProducts),
                ...Object.keys(newProductEntries),
              ]),
              totalProductsFetched:
                state.totalProductsFetched + newProducts.length,
            }));
          }),
        );
      }),
    );
  })(this.products$);

  readonly updateMediaCount = this.effect<number>((mediaCount$) => {
    return mediaCount$.pipe(
      filter((mediaCount) => isNonNullish(mediaCount)),
      tap((mediaCount) => {
        this.setMediaCount(mediaCount as number);
      }),
    );
  })(this.store.select(selectMediaRelationsMediaCount));

  readonly activeAccountChange = this.effect<string | null>((accountId$) => {
    return accountId$.pipe(
      tap(() => {
        this.patchState({ suggestions: [] });
      }),
    );
  })(this.store.select(selectActiveAccountId));

  getMediaRelationSuggests(products: Product[]) {
    this.products$.next(products);
  }

  readonly suggestions$ = this.select((state) => state.suggestions);

  readonly resetState = this.updater(() => ({
    suggestions: [],
    mediaCount: 1,
    orderType: OrderType.None,
    fetchedProducts: new Set(),
    totalProductsFetched: 0,
  }));
}
